import React from "react";
import Select, { GroupBase, MultiValue, OptionProps, Props, SingleValue, components } from 'react-select';
import { humanReadableSort } from "../../Utilities";

export interface KaSelectOption {
  value: string;
  label: string;
  isDisabled?: boolean;
};

// Function is useful for solving type problems in onChange handler
export function isMultiValue<T>(arg: MultiValue<T> | SingleValue<T>): arg is MultiValue<T> {
  return Array.isArray(arg);
}

export function toKaSelectOptions<T, Option extends KaSelectOption>(
  items: T[] | null | undefined,
  valueKey: keyof T,
  labelKey: keyof T,
  options?: {
    labelFn?: (item: T) => string;
    sortByLabel?: boolean;
  }): Option[] {
  const labelFn = options?.labelFn;
  const sortByLabel = options?.sortByLabel ?? false;

  if (!items?.length) return [] as Option[];

  const mapped = items.map(item => ({
    value: String(item[valueKey]),
    label: labelFn ? labelFn(item) : String(item[labelKey]),
  } as Option));

  return sortByLabel
    ? humanReadableSort(mapped, "label")
    : mapped;
}

export const KaSelect = React.forwardRef(function KaSelect<
  Option extends KaSelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>>(props: Props<Option, IsMulti, Group>, ref: any) {

  const IndicatorSeparator = () => (
    <></>
  )

  const IconOption = (props: OptionProps<Option>) => (
    <components.Option {...props}>
      <div className={props.isSelected ? "selected-option" : ""}>
        {props.data.label}
      </div>
    </components.Option>
  );

  return <Select
    {...props}
    ref={ref}
    components={props.isClearable
      ? { Option: IconOption }
      : { IndicatorSeparator, Option: IconOption }}
    styles={{
      option: (provided, state) => ({
        ...provided,
        color: state.isDisabled ? '#677788' : '#282a2c',
        backgroundColor:
          state.isDisabled
            ? '#f2f2f2'
            : state.isSelected ? '#ebedf1' : 'inherit',
        '&:hover': { backgroundColor: state.isDisabled ? '#f2f2f2' : '#ebedf1' }
      }),
      control: (base) => ({
        ...base,
        borderColor: "lightgrey",
        boxShadow: 'none',
        "&:hover": { borderColor: 'lightgrey' }
      }),
    }}
  />

});